import {css, html} from '@isceco/widget-library2/external/lit'
import {unsafeHTML} from '@isceco/widget-library2/external/lit/directives/unsafe-html.js'
import '@isceco/widget-library2/basic-elements/Title/Title'
import '@isceco/widget-library2/basic-elements/Dropdown/Dropdown'
import '@isceco/widget-library2/basic-elements/NumberInput/NumberInput'
import '@isceco/widget-library2/basic-elements/TextInput/TextInput'
import '@isceco/widget-library2/basic-elements/Card/Card'
import '@isceco/widget-library2/basic-elements/Popup/Popup.js'
import '@isceco/widget-library2/basic-elements/Form/Form'
import '@isceco/widget-library2/basic-elements/Button/Button'
import WebComponent from '../../WebComponent.js'
import LandService from '../../services/LandService.js'
import BrancheService from '../../services/BrancheService.js'
import GemeindeService from '../../services/GemeindeService.js'
import BewilligungService from '../../services/BewilligungService.js'
import ConfigUtil from '../../services/ConfigUtil.js'


const EINSATZORT_VALUE_SEP = '\u001f'

function gemeindeOptionValue(gemeinde) {
  return [gemeinde.plz, gemeinde.kanton, gemeinde.ort].join(EINSATZORT_VALUE_SEP)
}

function formatGemeindeDropdownLabel(gemeinde) {
  return `${gemeinde.plz} - ${gemeinde.ort} (${gemeinde.kanton})`
}

export default class BewilligungAbklaeren extends WebComponent {
  landItems = []
  brancheItems = []
  gemeindeItems = []
  showResult = false
  ergebnisData = null
  lastRequestData = null
  isLoading = false
  isPdfLoading = false
  mindestlohnUrl = null

  currentSitzDesUnternehmens = null
  currentStaatsangehoerigkeit = null
  arbeitsmarktAntwort = null
  arbeitsmarktJaLand = null
  lastArbeitsmarktAntwort = null
  lastArbeitsmarktJaLand = null
  lastOriginalStaatsangehoerigkeit = null
  selectedEinsatzortGemeinde = null
  isRefreshingLanguageData = false

  get translationFile() {
    return './views/BewilligungAbklaeren/i18n.json'
  }

  connectedCallback() {
    super.connectedCallback()
    new ConfigUtil().retrieveConfiguration().then(config => {
      this.mindestlohnUrl = config.mindestlohnUrl || this.i18n.translate('footer.mindestlohn.link')
      this.render()
    })
    this.loadDropdownData()
    this.render()
  }

  async loadDropdownData() {
    const [landsResult, branchesResult] = await Promise.allSettled([
      new LandService().list(),
      new BrancheService().list()
    ])

    const lands = landsResult.status === 'fulfilled' ? landsResult.value : null
    const branches = branchesResult.status === 'fulfilled' ? branchesResult.value : null

    if (lands) {
      this.landItems = lands.map(land => ({
        value: land.code,
        name: this.getTranslatedText(land)
      })).sort((a, b) => a.name.localeCompare(b.name))
      this.landItems.push({ value: 'NONE', name: this.i18n.translate('abklaeren.keinesderaufgefuehrten.label') })
    }

    if (branches) {
      this.brancheItems = branches.map(branche => ({
        value: String(branche.id),
        name: this.getTranslatedText(branche)
      })).sort((a, b) => a.name.localeCompare(b.name))
      this.brancheItems.push({ value: 'ANDERE', name: this.i18n.translate('abklaeren.anderebranche.label') })
    }

    this.render()
  }

  async refreshLanguageDependentData() {
    if (this.isRefreshingLanguageData) {
      return
    }

    this.isRefreshingLanguageData = true
    try {
      await this.loadDropdownData()

      // Refresh result messages so visible result text switches language immediately.
      if (this.showResult && this.lastRequestData) {
        const result = await new BewilligungService().calculate(this.lastRequestData)
        if (result) {
          this.ergebnisData = result
        }
      }
    } catch {
      // Keep current state if backend refresh fails.
    } finally {
      this.isRefreshingLanguageData = false
      this.render()
    }
  }

  reload() {
    super.reload()
    this.refreshLanguageDependentData()
  }

  getTranslatedText(item) {
    const lang = getLanguage()
    return item[lang] || item.de || ''
  }

  get showArbeitsmarktZulassung() {
    return this.currentSitzDesUnternehmens &&
      this.currentSitzDesUnternehmens !== 'NONE' &&
      this.currentSitzDesUnternehmens !== 'CH' &&
      this.currentStaatsangehoerigkeit === 'NONE'
  }

  get landItemsOhneNone() {
    return this.landItems.filter(item => item.value !== 'NONE')
  }

  formatEinsatzortAnzeige(inputs) {
    if (!inputs?.einsatzortPlz) {
      return ''
    }
    if (inputs.einsatzortKanton && inputs.einsatzortOrt) {
      return `${inputs.einsatzortPlz} - ${inputs.einsatzortOrt} (${inputs.einsatzortKanton})`
    }
    return this.gemeindeItems.find(item =>
      item.plz === inputs.einsatzortPlz &&
      item.kanton === inputs.einsatzortKanton &&
      item.ort === inputs.einsatzortOrt
    )?.name || inputs.einsatzortPlz
  }

  handleSitzChange(e) {
    const val = e.detail?.value ?? e.target?.value
    if (val === undefined || val === null) {
      return
    }
    this.currentSitzDesUnternehmens = val
    this.arbeitsmarktAntwort = null
    this.arbeitsmarktJaLand = (val && val !== 'NONE' && val !== 'CH') ? val : null
    this.render()
  }

  syncSelectedEinsatzortWithItems() {
    const sel = this.selectedEinsatzortGemeinde
    if (!sel || !this.gemeindeItems.length) {
      return
    }
    const stillThere = this.gemeindeItems.some(
      i => i.plz === sel.plz && i.kanton === sel.kanton && i.ort === sel.ort
    )
    if (!stillThere) {
      this.selectedEinsatzortGemeinde = null
    }
  }

  handleEinsatzortChange(e) {
    const val = e.detail?.value ?? e.target?.value
    if (val == null || val === '') {
      this.selectedEinsatzortGemeinde = null
      this.render()
      return
    }
    const item = this.gemeindeItems.find(i => i.value === val)
    this.selectedEinsatzortGemeinde = item
      ? { plz: item.plz, kanton: item.kanton, ort: item.ort }
      : null
    this.render()
  }

  async handleGemeindeSearch(searchTerm) {
    if (!searchTerm || searchTerm.length < 2) {
      this.gemeindeItems = []
      this.selectedEinsatzortGemeinde = null
      this.render()
      return
    }

    try {
      const results = await new GemeindeService().search(searchTerm)

      if (results && results.length > 0) {
        this.gemeindeItems = results.map(gemeinde => ({
          value: gemeindeOptionValue(gemeinde),
          name: formatGemeindeDropdownLabel(gemeinde),
          plz: gemeinde.plz,
          kanton: gemeinde.kanton,
          ort: gemeinde.ort
        }))
        this.syncSelectedEinsatzortWithItems()
      } else {
        this.gemeindeItems = []
        this.selectedEinsatzortGemeinde = null
      }
      this.render()
    } catch {
      this.gemeindeItems = []
      this.selectedEinsatzortGemeinde = null
      this.render()
    }
  }

  validateArbeitsmarktZulassung() {
    if (!this.arbeitsmarktAntwort) {
      showAlert('Fehler', this.i18n.translate('abklaeren.arbeitsmarktzulassung.validation.required'), 'error')
      return false
    }
    if (this.arbeitsmarktAntwort === 'ja' && !this.arbeitsmarktJaLand) {
      showAlert('Fehler', this.i18n.translate('abklaeren.arbeitsmarktzulassung.validation.landRequired'), 'error')
      return false
    }
    return true
  }

  applyArbeitsmarktOverrides({ sitzDesUnternehmens, staatsangehoerigkeit }) {
    if (this.arbeitsmarktAntwort === 'ja') { return { sitzDesUnternehmens, staatsangehoerigkeit: this.arbeitsmarktJaLand }}
    if (this.arbeitsmarktAntwort === 'nein') { return { sitzDesUnternehmens: 'NONE', staatsangehoerigkeit }}
    return { sitzDesUnternehmens, staatsangehoerigkeit }
  }

  async handleErgebnisAnzeigen() {
    const values = this.querySelector('#bewilligung-form').getValues()
    const { sitzDesUnternehmens, staatsangehoerigkeit, brancheDerTaetigkeit, dauerDesEinsatzes, einsatzort } = values

    if (this.showArbeitsmarktZulassung && !this.validateArbeitsmarktZulassung()) { return }

    const overrides = this.showArbeitsmarktZulassung
      ? this.applyArbeitsmarktOverrides({ sitzDesUnternehmens, staatsangehoerigkeit })
      : { sitzDesUnternehmens, staatsangehoerigkeit }

    if (!this.selectedEinsatzortGemeinde || gemeindeOptionValue(this.selectedEinsatzortGemeinde) !== einsatzort) {
      showAlert('Fehler', this.i18n.translate('abklaeren.einsatzort.validation.selectFromList'), 'error')
      return
    }
    const { plz, kanton, ort } = this.selectedEinsatzortGemeinde
    const requestData = {
      ...overrides,
      branche: brancheDerTaetigkeit,
      dauerDesEinsatzes: Number.parseInt(dauerDesEinsatzes),
      einsatzortPlz: plz,
      einsatzortKanton: kanton,
      einsatzortOrt: ort
    }

    this.lastArbeitsmarktAntwort = this.showArbeitsmarktZulassung ? this.arbeitsmarktAntwort : null
    this.lastArbeitsmarktJaLand = this.showArbeitsmarktZulassung && this.arbeitsmarktAntwort === 'ja' ? this.arbeitsmarktJaLand : null
    this.lastOriginalStaatsangehoerigkeit = staatsangehoerigkeit

    this.isLoading = true
    this.render()

    try {
      const result = await new BewilligungService().calculate(requestData)
      this.ergebnisData = result
      this.lastRequestData = requestData
      this.showResult = true
    } catch {
      showAlert('Fehler', 'Fehler bei der Berechnung', 'error')
    } finally {
      this.isLoading = false
      this.render()
      if (this.showResult) {
        requestAnimationFrame(() => {
          const el = this.querySelector('.result-section')
          if (el) {
            const top = el.getBoundingClientRect().top + window.scrollY - 80
            window.scrollTo({ top, behavior: 'smooth' })
          }
        })
      }
    }
  }

  async handleDrucken() {
    if (!this.lastRequestData || !this.ergebnisData) { return }

    const inputs = this.ergebnisData.userInputs

    const sitzName = this.landItems.find(item => item.value === inputs.sitzDesUnternehmens)?.name || inputs.sitzDesUnternehmens
    const displayStaatsangehoerigkeit = this.lastOriginalStaatsangehoerigkeit ?? inputs.staatsangehoerigkeit
    const staatsName = this.landItems.find(item => item.value === displayStaatsangehoerigkeit)?.name || displayStaatsangehoerigkeit
    const brancheName = this.brancheItems.find(item => item.value === inputs.branche)?.name || inputs.branche
    const einsatzortName = this.formatEinsatzortAnzeige(inputs)
    const arbeitsmarktZulassungName = this.lastArbeitsmarktJaLand
      ? this.landItems.find(item => item.value === this.lastArbeitsmarktJaLand)?.name || this.lastArbeitsmarktJaLand
      : ''

    const pdfRequest = {
      request: this.lastRequestData,
      staatsangehoerigkeitName: staatsName,
      sitzName,
      brancheName,
      einsatzortName,
      arbeitsmarktZulassungName
    }

    this.isPdfLoading = true
    this.render()
    try {
      await new BewilligungService().downloadPdf(pdfRequest)
    } finally {
      this.isPdfLoading = false
      this.render()
    }
  }

  get css() {
    return css`
      .two-col {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        column-gap: 16px;
        margin-bottom: 1rem;
      }

      .field-label {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 0.5rem;
      }

      .field-label-text {
        font-weight: bold;
      }

      .info-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: var(--isceco-color-blue-500);
        color: white;
        font-size: 0.75rem;
        cursor: help;
      }

      .field-wrapper {
        margin-bottom: 1.5rem;
      }

      .field-wrapper isceco-dropdown,
      .field-wrapper isceco-number-input {
        max-width: 400px;
        width: 100%;
      }

      .popup-content {
        padding: 1rem;
        max-width: 300px;
        line-height: 1.5;
      }

      .hint-text a,
      .result-intro a {
        color: var(--isceco-color-blue-500);
        text-decoration: underline;
      }

      .hint-text a:hover,
      .result-intro a:hover {
        color: var(--isceco-color-blue-700);
      }

      .result-cards {
        display: grid;
        grid-template-columns: 1fr 2fr;
        gap: 2rem;
      }

      .result-cards.two-contacts {
        grid-template-columns: 1fr 1fr;
      }

      .result-cards isceco-card {
        min-width: 0;
      }

      .result-cards .card-content {
        padding: 1.5rem;
        overflow-x: auto;
        max-width: 100%;
        box-sizing: border-box;
      }

      .result-intro,
      .result-hints,
      .legal-notice,
      .legal-notice-content,
      .result-date {
        font-size: 1rem;
        font-weight: 400;
        line-height: 1.6;
      }

      .result-hints h3 { margin-bottom: 0.5rem; }

      .result-hints ul {
        list-style: disc;
        margin-left: 1.5rem;
        line-height: 1.6;
        margin-bottom: 0.75rem;
      }

      .result-hints p { margin: 0; line-height: 1.6; }
      .result-hints a { color: var(--isceco-color-blue-500); text-decoration: underline; }
      .result-intro p { margin: 0 0 1rem 0; }
      .result-intro p:last-child { margin-bottom: 0; }
      .legal-notice-content p { margin: 0 0 1em 0; }
      .legal-notice summary { font-size: 1rem; font-weight: 400; }
      .legal-notice summary strong { font-weight: 700; }
      .kontakt-item { line-height: 1.6; flex: 0 0 auto; max-width: 280px; word-break: break-word; }
      .kontakt-item a { color: var(--isceco-color-blue-500); text-decoration: underline; }

      .user-inputs-table tr { border-bottom: 1px solid #f0f0f0; }
      .user-inputs-table tr:last-child { border-bottom: none; }

      .user-inputs-table td {
        padding: 0.5rem 0;
        vertical-align: top;
        line-height: 1.6;
      }

      .user-inputs-table td.label {
        font-weight: bold;
        color: #333;
        padding-right: 1rem;
        width: 60%;
      }

      .user-inputs-table td.value { color: #666; width: 40%; word-wrap: break-word; }

      @media (max-width: 768px) {
        .two-col { grid-template-columns: 1fr; gap: 0; }

        .result-cards,
        .result-cards.two-contacts {
          grid-template-columns: 1fr;
        }

        .user-inputs-table td.label,
        .user-inputs-table td.value { width: 50%; }
      }

      .arbeitsmarkt-options {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      .arbeitsmarkt-option {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        cursor: pointer;
        flex-wrap: wrap;
      }

      .arbeitsmarkt-option input[type="radio"] {
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        cursor: pointer;
        accent-color: var(--isceco-color-blue-500);
      }

      .arbeitsmarkt-option isceco-dropdown {
        max-width: 300px;
        width: 100%;
      }


      .footer-bottom {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 0.5rem;
        padding-top: 0.75rem;
        flex: 0 0 100%;
      }

      .footer-version {
        opacity: 0.8;
      }

      .page-wrapper {
        display: flex;
        flex-direction: column;
        flex: 1;
        max-width: 1200px;
        margin: 0 auto;
        width: 100%;
      }

      .relevant-links {
        margin-top: auto;
        padding-top: 1rem;
        border-top: 1px solid #ddd;
      }

      .relevant-links-title {
        font-weight: bold;
        margin-bottom: 1rem;
        color: inherit;
      }

      .relevant-links-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 1rem;
      }

      .card-description {
        color: var(--isceco-color-black-500, #333);
        margin: 0.4rem 0 0;
        line-height: 1.4;
      }
    `
  }

  getTemplate() {
    return html`
      <div class="page-wrapper">
        <isceco-title id="page-title"
                      text="${this.i18n.translate('page.title')}"
        ></isceco-title>

        <p class="intro-text" style="margin-bottom: 2rem; line-height: 1.6;">${this.i18n.translate('abklaeren.paragraph1.title')}</p>

        <isceco-form
          id="bewilligung-form"
          submit-button-text="${this.i18n.translate('abklaeren.button.ergebnisanzeigen')}"
          ?disabled="${this.isLoading}"
          @submit="${() => this.handleErgebnisAnzeigen()}"
        >
          <div class="two-col" slot="form-elements">
            <!-- Left Column -->
            <div style="min-width: 0;">
                <!-- Sitz des Unternehmens -->
                <div class="field-wrapper">
                  <div class="field-label">
                    <span class="field-label-text">${this.i18n.translate('abklaeren.sitzdesunternehmens.dropdown.title')}</span>
                    <isceco-popup direction="right">
                      <span slot="wrapper" class="info-icon">i</span>
                      <div slot="content" class="popup-content">
                        ${this.i18n.translate('abklaeren.sitzdesunternehmens.dropdown.popup')}
                      </div>
                    </isceco-popup>
                  </div>
                  <isceco-dropdown
                    name="sitzDesUnternehmens"
                    .items="${this.landItems}"
                    @change="${e => this.handleSitzChange(e)}"
                    required
                  ></isceco-dropdown>
                </div>

                <!-- Staatsangehörigkeit des Arbeitsnehmers -->
                <div class="field-wrapper">
                  <div class="field-label">
                    <span class="field-label-text">${this.i18n.translate('abklaeren.staatsangehörigkeit.dropdown.title')}</span>
                    <isceco-popup direction="up">
                      <span slot="wrapper" class="info-icon">i</span>
                      <div slot="content" class="popup-content">
                        ${this.i18n.translate('abklaeren.staatsangehörigkeit.dropdown.popup')}
                      </div>
                    </isceco-popup>
                  </div>
                  <isceco-dropdown
                    name="staatsangehoerigkeit"
                    .items="${this.landItems}"
                    @change="${e => {
                      const val = e.detail?.value ?? e.target?.value
                      if (val !== undefined && val !== null) {
                        this.currentStaatsangehoerigkeit = val || null
                        this.arbeitsmarktAntwort = null
                        this.arbeitsmarktJaLand = this.currentSitzDesUnternehmens
                        this.render()
                      }
                    }}"
                    required
                  ></isceco-dropdown>
                </div>

                <!-- Arbeitsmarkt-Zulassung (conditional) -->
                ${this.showArbeitsmarktZulassung ? html`
                  <div class="field-wrapper">
                    <div class="field-label">
                      <span class="field-label-text">${this.i18n.translate('abklaeren.arbeitsmarktzulassung.question')}</span>
                    </div>
                    <div class="arbeitsmarkt-options">
                      <label class="arbeitsmarkt-option">
                        <input
                          type="radio"
                          name="arbeitsmarktZulassung"
                          value="ja"
                          .checked="${this.arbeitsmarktAntwort === 'ja'}"
                          @change="${() => { this.arbeitsmarktAntwort = 'ja'; this.render() }}"
                        >
                        <span>${this.i18n.translate('abklaeren.arbeitsmarktzulassung.ja')}</span>
                        ${this.renderArbeitsmarktJaLandDropdown()}
                      </label>
                      <label class="arbeitsmarkt-option">
                        <input
                          type="radio"
                          name="arbeitsmarktZulassung"
                          value="nein"
                          .checked="${this.arbeitsmarktAntwort === 'nein'}"
                          @change="${() => { this.arbeitsmarktAntwort = 'nein'; this.render() }}"
                        >
                        <span>${this.i18n.translate('abklaeren.arbeitsmarktzulassung.nein')}</span>
                      </label>
                    </div>
                  </div>
                ` : ''}
              </div>

              <!-- Right Column -->
              <div style="min-width: 0;">
                <!-- Branche der Tätigkeit in der Schweiz -->
                <div class="field-wrapper">
                  <div class="field-label">
                    <span class="field-label-text">${this.i18n.translate('abklaeren.branchedertaetigkeit.dropdown.title')}</span>
                    <isceco-popup direction="up">
                      <span slot="wrapper" class="info-icon">i</span>
                      <div slot="content" class="popup-content">
                        ${this.i18n.translate('abklaeren.branchedertaetigkeit.dropdown.popup')}
                      </div>
                    </isceco-popup>
                  </div>
                  <isceco-dropdown
                    name="brancheDerTaetigkeit"
                    .items="${this.brancheItems}"
                    required
                  ></isceco-dropdown>
                </div>

                <!-- Dauer des Einsatzes -->
                <div class="field-wrapper">
                  <div class="field-label">
                    <span class="field-label-text">${this.i18n.translate('abklaeren.dauerdeseinsatzes.input.title')}</span>
                    <isceco-popup direction="up">
                      <span slot="wrapper" class="info-icon">i</span>
                      <div slot="content" class="popup-content">
                        ${this.i18n.translate('abklaeren.dauerdeseinsatzes.input.popup')}
                      </div>
                    </isceco-popup>
                  </div>
                  <isceco-number-input
                    name="dauerDesEinsatzes"
                    min="1"
                    maximum-fraction-digits="0"
                    required
                  ></isceco-number-input>
                </div>

                <!-- Einsatzort in der Schweiz (PLZ) -->
                <div class="field-wrapper">
                  <div class="field-label">
                    <span class="field-label-text">${this.i18n.translate('abklaeren.einsatzort.dropdown.title')}</span>
                    <isceco-popup direction="up">
                      <span slot="wrapper" class="info-icon">i</span>
                      <div slot="content" class="popup-content">
                        ${this.i18n.translate('abklaeren.einsatzort.dropdown.popup')}
                      </div>
                    </isceco-popup>
                  </div>
                  <isceco-dropdown
                    name="einsatzort"
                    .items="${this.gemeindeItems}"
                    searchable
                    @change="${e => this.handleEinsatzortChange(e)}"
                    @input="${e => {
                      const dropdown = e.target
                      const shadowRoot = dropdown.shadowRoot
                      const searchInput = shadowRoot?.querySelector('input[type="text"]') || shadowRoot?.querySelector('input')
                      const searchValue = searchInput?.value || ''

                      if (searchValue.length >= 2 || searchValue.length === 0) {
                        this.handleGemeindeSearch(searchValue)
                      }
                    }}"
                    required
                  ></isceco-dropdown>
                </div>

              </div>
            </div>
        </isceco-form>

        <p class="hint-text" style="margin-top: 2rem; line-height: 1.6;">
          <strong>${this.i18n.translate('abklaeren.hinweis.title')}</strong>
          ${this.i18n.translate('abklaeren.hinweis.text1')}
          <a href="${this.i18n.translate('abklaeren.hinweis.link.url')}" target="_blank" rel="noopener noreferrer">
            ${this.i18n.translate('abklaeren.hinweis.link.text')}
          </a>
        </p>

        ${this.showResult ? this.renderResult() : ''}

        <div class="relevant-links">
          <p class="relevant-links-title">${this.i18n.translate('footer.relevant-links.title')}</p>
          <div class="relevant-links-grid">
            <isceco-card>
              <isceco-link url="${this.i18n.translate('footer.startseite-entsendung.link')}"
                           text="${this.i18n.translate('footer.startseite-entsendung')}"
                           target="_blank"></isceco-link>
              <p class="card-description">${this.i18n.translate('footer.startseite-entsendung.description')}</p>
            </isceco-card>
            <isceco-card>
              <isceco-link url="${this.i18n.translate('footer.meldeverfahren-easygov.link')}"
                           text="${this.i18n.translate('footer.meldeverfahren-easygov')}"
                           target="_blank"></isceco-link>
              <p class="card-description">${this.i18n.translate('footer.meldeverfahren-easygov.description')}</p>
            </isceco-card>
            <isceco-card>
              <isceco-link url="${this.mindestlohnUrl || this.i18n.translate('footer.mindestlohn.link')}"
                           text="${this.i18n.translate('footer.mindestlohn')}"
                           target="_blank"></isceco-link>
              <p class="card-description">${this.i18n.translate('footer.mindestlohn.description')}</p>
            </isceco-card>
            <isceco-card>
              <isceco-link url="${this.i18n.translate('footer.lohnspanne.link')}"
                           text="${this.i18n.translate('footer.lohnspanne')}"
                           target="_blank"></isceco-link>
              <p class="card-description">${this.i18n.translate('footer.lohnspanne.description')}</p>
            </isceco-card>
          </div>
        </div>
      </div>
    `
  }

  renderArbeitsmarktJaLandDropdown() {
    if (this.arbeitsmarktAntwort !== 'ja') { return '' }
    return html`
      <isceco-dropdown
        name="arbeitsmarktJaLand"
        .items="${this.landItemsOhneNone}"
        .value="${this.arbeitsmarktJaLand}"
        @change="${e => { this.arbeitsmarktJaLand = e.detail?.value ?? e.target?.value; this.render() }}"
      ></isceco-dropdown>
    `
  }

  renderResult() {
    if (!this.ergebnisData){
       return ''
    }

    const currentDate = new Date().toLocaleDateString('de-CH')
    const msg = this.ergebnisData.messages || {}
    const t = key => msg[key] || ''
    const twoContacts = (this.ergebnisData?.kontakte?.length ?? 0) >= 2

    return html`
      <div class="result-section" style="margin-top: 2rem; padding-top: 2rem; border-top: 1px solid #ddd;">
        <h2 style="font-size: 1.5rem; margin-bottom: 1.5rem;">${this.i18n.translate('ergebnis.title')}</h2>

        <div class="result-intro" style="margin-bottom: 1.5rem;">
          ${unsafeHTML(t('ergebnis.intro'))}
        </div>

        <div class="result-cards ${twoContacts ? 'two-contacts' : ''}" style="margin-bottom: 2rem;">
          <isceco-card style="overflow: hidden;">
            <div class="card-content">
              <h3 style="font-size: 1.1rem; margin: 0 0 1rem 0; color: var(--isceco-color-blue-500);">${t('ergebnis.kontaktinformation.title')}</h3>
              ${this.renderKontakte()}
            </div>
          </isceco-card>

          <isceco-card style="overflow: hidden;">
            <div class="card-content">
              <h3 style="font-size: 1.1rem; margin: 0 0 1rem 0; color: var(--isceco-color-blue-500);">${t('ergebnis.ihreangaben.title')}</h3>
              ${this.renderUserInputs()}
            </div>
          </isceco-card>
        </div>

        <div class="result-hints" style="margin-bottom: 2rem;">
          ${unsafeHTML(t('ergebnis.hinweise'))}
        </div>

        <details class="legal-notice" style="margin-bottom: 2rem;">
          <summary style="cursor: pointer; padding: 0.5rem 0;"><strong>${t('ergebnis.rechtlicherhinweis.title')}</strong></summary>
          <div class="legal-notice-content">
            ${unsafeHTML(t('ergebnis.rechtlicherhinweis.content'))}
          </div>
        </details>

        <p class="result-date" style="margin-top: 2rem;">
          ${t('ergebnis.datum.label')} ${currentDate}
        </p>

        <div style="display: flex; justify-content: flex-end; margin-top: 1.5rem;">
          <isceco-button
            variant="primary"
            icon="print"
            text="${this.isPdfLoading ? '...' : this.i18n.translate('ergebnis.button.drucken')}"
            ?disabled="${this.isPdfLoading}"
            @click="${() => this.handleDrucken()}"
          ></isceco-button>
        </div>
      </div>
    `
  }

  renderKontakte() {
    if (!this.ergebnisData?.kontakte || this.ergebnisData.kontakte.length === 0) {
      return html`<p>Keine Kontaktinformationen verfügbar</p>`
    }

    return html`
      <div class="kontakte-wrapper" style="display: flex; flex-direction: row; flex-wrap: wrap; gap: 1.5rem;">
        ${this.ergebnisData.kontakte.map(kontakt => html`
          <div class="kontakt-item">
            <strong style="color: var(--isceco-color-blue-500);">${kontakt.name}</strong><br>
            ${kontakt.zusatzname ? html`${kontakt.zusatzname}<br>` : ''}
            ${kontakt.strasse}<br>
            ${kontakt.zusatz ? html`${kontakt.zusatz}<br>` : ''}
            ${kontakt.plz} ${kontakt.ort}<br>
            ${kontakt.tel ? html`Tel.: ${kontakt.tel}<br>` : ''}
            ${kontakt.fax ? html`Fax: ${kontakt.fax}<br>` : ''}
            ${kontakt.email ? html`<a href="mailto:${kontakt.email}">E-Mail</a><br>` : ''}
            ${kontakt.url ? html`<a href="${kontakt.url}" target="_blank" rel="noopener noreferrer">Internet</a>` : ''}
          </div>
        `)}
      </div>
    `
  }

  renderUserInputs() {
    if (!this.ergebnisData?.userInputs) {
      return html`<p>Keine Angaben verfügbar</p>`
    }

    const inputs = this.ergebnisData.userInputs

    // Find display names from items
    const sitzName = this.landItems.find(item => item.value === inputs.sitzDesUnternehmens)?.name || inputs.sitzDesUnternehmens
    const displayStaatsangehoerigkeit = this.lastOriginalStaatsangehoerigkeit ?? inputs.staatsangehoerigkeit
    const staatsName = this.landItems.find(item => item.value === displayStaatsangehoerigkeit)?.name || displayStaatsangehoerigkeit
    const brancheName = this.brancheItems.find(item => item.value === inputs.branche)?.name || inputs.branche
    const einsatzortName = this.formatEinsatzortAnzeige(inputs)

    const arbeitsmarktJaLandName = this.lastArbeitsmarktJaLand
      ? this.landItems.find(item => item.value === this.lastArbeitsmarktJaLand)?.name || this.lastArbeitsmarktJaLand
      : ''

    return html`
      <table class="user-inputs-table" style="width: 100%; border-collapse: collapse; table-layout: fixed;">
        <tr>
          <td class="label">${this.i18n.translate('abklaeren.sitzdesunternehmens.dropdown.title')}:</td>
          <td class="value">${sitzName}</td>
        </tr>
        <tr>
          <td class="label">${this.i18n.translate('abklaeren.staatsangehörigkeit.dropdown.title')}:</td>
          <td class="value">${staatsName}</td>
        </tr>
        ${this.lastArbeitsmarktAntwort === 'ja' ? html`
          <tr>
            <td class="label">${this.i18n.translate('abklaeren.arbeitsmarktzulassung.label')}:</td>
            <td class="value">${arbeitsmarktJaLandName}</td>
          </tr>
        ` : ''}
        <tr>
          <td class="label">${this.i18n.translate('abklaeren.branchedertaetigkeit.dropdown.title')}:</td>
          <td class="value">${brancheName}</td>
        </tr>
        <tr>
          <td class="label">${this.i18n.translate('abklaeren.dauerdeseinsatzes.label')}:</td>
          <td class="value">${inputs.dauerDesEinsatzes}${this.i18n.translate('abklaeren.dauerdeseinsatzes.suffix')}</td>
        </tr>
        <tr>
          <td class="label">${this.i18n.translate('abklaeren.einsatzort.dropdown.title')}:</td>
          <td class="value">${einsatzortName}</td>
        </tr>
      </table>
    `
  }
}

customElements.define('bewilligung-frontend-bewilligung-abklaeren', BewilligungAbklaeren)
